import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Package, Plus, Loader2, Search, Save, X, MoreHorizontal, Trash2, GitMerge, AlertTriangle } from "lucide-react";

const TIPOS = ["Gola e punho juntos", "Gola separado", "Punho separado"];

const formatKg = (v) =>
  (v == null || Number.isNaN(v)) ? "—" : `${Number(v).toFixed(2).replace(".", ",")} kg`;

export default function EstoqueGolaPunho() {
  const { user, hasPerm } = useAuth();
  const podeEntrada = user?.is_master || hasPerm("estoque_gp:entrada");
  const podeExcluir = user?.is_master || hasPerm("estoque_gp:excluir");
  const podeMesclar = user?.is_master || hasPerm("estoque_gp:mesclar");
  const podeCadastrarCor = user?.is_master || hasPerm("cores:gerenciar");
  const temAcaoExtra = podeExcluir || podeMesclar;

  const [produtos, setProdutos] = useState([]);
  const [cores, setCores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busca, setBusca] = useState("");
  const [tipoFiltro, setTipoFiltro] = useState("");

  const [showEntrada, setShowEntrada] = useState(false);
  const [excluindo, setExcluindo] = useState(null); // produto completo
  const [mesclando, setMesclando] = useState(null); // produto origem
  const [menuAberto, setMenuAberto] = useState(null); // id do produto com dropdown aberto

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const [rProd, rCores] = await Promise.all([
        api.get("/gola-punho/produtos"),
        api.get("/cores"),
      ]);
      setProdutos(rProd.data || []);
      setCores(rCores.data || []);
    } catch (e) {
      const _m = apiError(e); if (_m) toast.error(_m);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const filtrados = useMemo(() => {
    const q = busca.trim().toLowerCase();
    return produtos.filter((p) => {
      if (tipoFiltro && p.tipo !== tipoFiltro) return false;
      if (q && !p.cor.toLowerCase().includes(q) && !p.tipo.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [produtos, busca, tipoFiltro]);

  const totalPorTipo = useMemo(() => {
    const acc = {};
    for (const p of produtos) {
      acc[p.tipo] = (acc[p.tipo] || 0) + Number(p.saldo_kg || 0);
    }
    return acc;
  }, [produtos]);

  const inputCls =
    "h-11 w-full rounded-md border-2 border-slate-300 bg-white px-3 text-sm font-medium outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200";

  return (
    <div data-testid="gp-page">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold tracking-tight text-slate-900 md:text-3xl">
            <Package size={28} /> Estoque de Gola e Punho
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Saldo a granel em kg por produto (tipo + cor). Estoque separado dos rolos de tecido.
          </p>
        </div>
        {podeEntrada && (
          <button
            onClick={() => setShowEntrada(true)}
            data-testid="gp-nova-entrada"
            className="flex items-center gap-2 rounded-md bg-slate-900 px-4 py-2.5 text-sm font-bold text-white hover:bg-slate-800"
          >
            <Plus size={16} /> Nova entrada
          </button>
        )}
      </div>

      {/* Totais por tipo */}
      <div className="mb-4 grid gap-3 sm:grid-cols-3" data-testid="gp-totais">
        {TIPOS.map((t) => (
          <div key={t} className="rounded-md border-2 border-slate-200 bg-white p-3">
            <div className="text-xs font-bold uppercase tracking-wider text-slate-500">{t}</div>
            <div className="mt-1 text-2xl font-bold font-mono text-slate-900" data-testid={`gp-total-${t}`}>
              {formatKg(totalPorTipo[t] || 0)}
            </div>
          </div>
        ))}
      </div>

      {/* Filtros */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[16rem]">
          <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            className={`${inputCls} pl-9`}
            placeholder="Buscar por cor ou tipo…"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            data-testid="gp-busca"
          />
        </div>
        <select
          value={tipoFiltro}
          onChange={(e) => setTipoFiltro(e.target.value)}
          className={`${inputCls} w-auto`}
          data-testid="gp-filtro-tipo"
        >
          <option value="">Todos os tipos</option>
          {TIPOS.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="flex justify-center py-16 text-slate-400"><Loader2 className="animate-spin" /></div>
      ) : filtrados.length === 0 ? (
        <div className="rounded-md border-2 border-dashed border-slate-300 bg-white p-10 text-center text-sm text-slate-500" data-testid="gp-empty">
          {produtos.length === 0
            ? "Nenhum produto ainda. Registre a primeira entrada."
            : "Nenhum produto corresponde ao filtro."}
        </div>
      ) : (
        <ul className="space-y-2" data-testid="gp-lista">
          {filtrados.map((p) => (
            <li
              key={p.id}
              className="flex flex-wrap items-center justify-between gap-2 rounded-md border-2 border-slate-200 bg-white p-4"
              data-testid={`gp-row-${p.id}`}
            >
              <div className="min-w-0 flex-1">
                <div className="text-xs font-bold uppercase tracking-wider text-slate-500">{p.tipo}</div>
                <div className="mt-0.5 flex flex-wrap items-center gap-2">
                  <span className="text-lg font-bold text-slate-900" data-testid={`gp-cor-${p.id}`}>{p.cor}</span>
                  {p.legado && (
                    <span
                      className="rounded-sm bg-slate-200 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-slate-600"
                      data-testid={`gp-badge-legado-${p.id}`}
                      title="Produto criado antes do cadastro central de cores"
                    >
                      legado
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <div className="text-xs font-medium uppercase tracking-wider text-slate-400">Saldo</div>
                  <div className="text-2xl font-bold font-mono text-slate-900" data-testid={`gp-saldo-${p.id}`}>
                    {formatKg(p.saldo_kg)}
                  </div>
                </div>
                {temAcaoExtra && (
                  <div className="relative">
                    <button
                      onClick={() => setMenuAberto(menuAberto === p.id ? null : p.id)}
                      className="rounded-md border-2 border-slate-200 p-2 text-slate-600 hover:bg-slate-100"
                      data-testid={`gp-acoes-${p.id}`}
                      title="Mais ações"
                    >
                      <MoreHorizontal size={14} />
                    </button>
                    {menuAberto === p.id && (
                      <>
                        <div className="fixed inset-0 z-10" onClick={() => setMenuAberto(null)} />
                        <div
                          className="absolute right-0 top-10 z-20 min-w-[13rem] rounded-md border-2 border-slate-200 bg-white p-1 shadow-lg"
                          data-testid={`gp-menu-${p.id}`}
                        >
                          {podeMesclar && (
                            <MenuItem
                              icon={GitMerge}
                              onClick={() => { setMenuAberto(null); setMesclando(p); }}
                              testid={`gp-menu-mesclar-${p.id}`}
                            >
                              Mesclar em outro
                            </MenuItem>
                          )}
                          {podeExcluir && (
                            <MenuItem
                              icon={Trash2}
                              onClick={() => { setMenuAberto(null); setExcluindo(p); }}
                              testid={`gp-menu-excluir-${p.id}`}
                              danger
                            >
                              Excluir produto
                            </MenuItem>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {showEntrada && (
        <EntradaModal
          cores={cores}
          podeCadastrarCor={podeCadastrarCor}
          onCorCriada={carregar}
          onClose={() => setShowEntrada(false)}
          onSaved={() => { setShowEntrada(false); carregar(); }}
        />
      )}
      {excluindo && (
        <ExcluirProdutoModal
          produto={excluindo}
          onClose={() => setExcluindo(null)}
          onSaved={() => { setExcluindo(null); carregar(); }}
        />
      )}
      {mesclando && (
        <MesclarProdutoModal
          origem={mesclando}
          todosProdutos={produtos}
          onClose={() => setMesclando(null)}
          onSaved={() => { setMesclando(null); carregar(); }}
        />
      )}
    </div>
  );
}

function MenuItem({ icon: Icon, children, onClick, testid, danger }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`flex w-full items-center gap-2 rounded-sm px-3 py-2 text-left text-sm font-medium transition-colors ${
        danger ? "text-red-700 hover:bg-red-50" : "text-slate-700 hover:bg-slate-100"
      }`}
    >
      <Icon size={14} /> {children}
    </button>
  );
}

// ────────────────────────── Modais ──────────────────────────

function EntradaModal({ cores, podeCadastrarCor, onCorCriada, onClose, onSaved }) {
  const [tipo, setTipo] = useState(TIPOS[0]);
  const [corId, setCorId] = useState("");
  const [peso, setPeso] = useState("");
  const [saving, setSaving] = useState(false);
  const [criandoCor, setCriandoCor] = useState(false);
  const [novaCor, setNovaCor] = useState("");
  const [salvandoCor, setSalvandoCor] = useState(false);

  const criarCorInline = async () => {
    const nome = novaCor.trim();
    if (!nome) { toast.error("Digite o nome da cor"); return; }
    setSalvandoCor(true);
    try {
      const r = await api.post("/cores", { nome });
      toast.success(`Cor "${r.data.nome}" cadastrada`);
      setCorId(r.data.id);
      setNovaCor("");
      setCriandoCor(false);
      onCorCriada && onCorCriada();
    } catch (e) { const _m = apiError(e); if (_m) toast.error(_m); }
    finally { setSalvandoCor(false); }
  };

  const submit = async () => {
    if (!corId) { toast.error("Selecione a cor"); return; }
    const p = parseFloat(String(peso).replace(",", "."));
    if (!Number.isFinite(p) || p <= 0) { toast.error("Peso inválido"); return; }
    setSaving(true);
    try {
      const r = await api.post("/gola-punho/entrada", { tipo, cor_id: corId, peso_kg: p });
      toast.success(`Entrada de ${p.toFixed(2).replace(".", ",")} kg em ${tipo} — ${r.data.produto.cor}`);
      onSaved();
    } catch (e) {
      const _m = apiError(e); if (_m) toast.error(_m);
    } finally {
      setSaving(false);
    }
  };

  const inputCls =
    "h-11 w-full rounded-md border-2 border-slate-300 bg-white px-3 text-sm font-medium outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200";

  return (
    <Modal onClose={onClose} testid="gp-entrada-modal" title="Nova entrada — Gola e Punho">
      <div className="space-y-4">
        <label className="block">
          <span className="mb-1 block text-sm font-semibold text-slate-700">Tipo</span>
          <select value={tipo} onChange={(e) => setTipo(e.target.value)} className={inputCls} data-testid="gp-entrada-tipo">
            {TIPOS.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </label>
        <div className="block">
          <div className="mb-1 flex items-center justify-between">
            <span className="text-sm font-semibold text-slate-700">Cor</span>
            {podeCadastrarCor && !criandoCor && (
              <button
                type="button"
                onClick={() => setCriandoCor(true)}
                data-testid="gp-entrada-nova-cor-btn"
                className="text-xs font-semibold text-blue-600 hover:text-blue-800"
              >
                + Cadastrar nova cor
              </button>
            )}
          </div>
          {criandoCor ? (
            <div className="flex items-center gap-2">
              <input
                type="text"
                className={inputCls}
                placeholder="Nome da nova cor"
                value={novaCor}
                onChange={(e) => setNovaCor(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") criarCorInline(); }}
                data-testid="gp-entrada-nova-cor-input"
                autoFocus
              />
              <button
                type="button"
                onClick={criarCorInline}
                disabled={salvandoCor || !novaCor.trim()}
                data-testid="gp-entrada-nova-cor-salvar"
                className="flex h-11 items-center gap-1 rounded-md bg-slate-900 px-3 text-sm font-bold text-white hover:bg-slate-800 disabled:opacity-50"
              >
                {salvandoCor ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              </button>
              <button
                type="button"
                onClick={() => { setCriandoCor(false); setNovaCor(""); }}
                className="flex h-11 items-center rounded-md border-2 border-slate-200 px-3 text-slate-600 hover:bg-slate-100"
                data-testid="gp-entrada-nova-cor-cancelar"
              >
                <X size={14} />
              </button>
            </div>
          ) : (
            <select
              value={corId}
              onChange={(e) => setCorId(e.target.value)}
              className={inputCls}
              data-testid="gp-entrada-cor"
              autoFocus
            >
              <option value="">Selecione a cor…</option>
              {cores.map((c) => (
                <option key={c.id} value={c.id}>{c.nome}</option>
              ))}
            </select>
          )}
          {cores.length === 0 && !criandoCor && (
            <div className="mt-2 rounded-md border-2 border-amber-200 bg-amber-50 p-2 text-xs font-semibold text-amber-800">
              Nenhuma cor cadastrada ainda. {podeCadastrarCor ? "Clique em '+ Cadastrar nova cor' acima." : "Peça a um administrador para cadastrar cores em Cadastro → Cores."}
            </div>
          )}
        </div>
        <label className="block">
          <span className="mb-1 block text-sm font-semibold text-slate-700">Peso (kg)</span>
          <input
            type="text"
            inputMode="decimal"
            className={`${inputCls} font-mono`}
            placeholder="Ex: 20 ou 12,5"
            value={peso}
            onChange={(e) => setPeso(e.target.value.replace(/[^0-9.,]/g, ""))}
            data-testid="gp-entrada-peso"
          />
          <span className="mt-1 block text-xs text-slate-500">
            Some as etiquetas dos rolos e lance o total. Se o produto já existir, o valor é somado ao saldo atual.
          </span>
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <button
            onClick={onClose}
            className="rounded-md border-2 border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100"
            data-testid="gp-entrada-cancelar"
            disabled={saving}
          >
            Cancelar
          </button>
          <button
            onClick={submit}
            disabled={saving || !corId}
            data-testid="gp-entrada-salvar"
            className="flex items-center gap-2 rounded-md bg-slate-900 px-4 py-2 text-sm font-bold text-white hover:bg-slate-800 disabled:opacity-50"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            Registrar entrada
          </button>
        </div>
      </div>
    </Modal>
  );
}

function ExcluirProdutoModal({ produto, onClose, onSaved }) {
  const [saving, setSaving] = useState(false);
  const saldo = Number(produto.saldo_kg || 0);
  const submit = async () => {
    setSaving(true);
    try {
      await api.delete(`/gola-punho/produtos/${produto.id}`);
      toast.success(`Produto excluído (saldo zerado: ${saldo.toFixed(2).replace(".", ",")} kg)`);
      onSaved();
    } catch (e) {
      const _m = apiError(e); if (_m) toast.error(_m);
    } finally {
      setSaving(false);
    }
  };
  return (
    <Modal onClose={onClose} testid="gp-excluir-modal" title="Excluir produto">
      <div className="space-y-4">
        <div className="rounded-md border-2 border-red-200 bg-red-50 p-3">
          <div className="flex items-start gap-2 text-sm font-bold text-red-800">
            <AlertTriangle size={16} className="mt-0.5 shrink-0" />
            <span>Ação irreversível. O produto e todo o seu saldo serão removidos.</span>
          </div>
        </div>
        <div className="rounded-md bg-slate-50 p-4">
          <div className="text-xs font-bold uppercase tracking-wider text-slate-500">{produto.tipo}</div>
          <div className="mt-0.5 text-lg font-bold text-slate-900">{produto.cor}</div>
          <div className="mt-3 text-xs font-medium uppercase tracking-wider text-slate-400">Saldo que será zerado</div>
          <div className="text-3xl font-bold font-mono text-red-700" data-testid="gp-excluir-saldo">
            {saldo.toFixed(2).replace(".", ",")} kg
          </div>
        </div>
        <p className="text-xs text-slate-500">
          A exclusão fica registrada no Jornal com o saldo perdido, seu nome e a data.
        </p>
        <div className="flex justify-end gap-2 pt-2">
          <button
            onClick={onClose}
            className="rounded-md border-2 border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100"
            data-testid="gp-excluir-cancelar"
            disabled={saving}
          >
            Cancelar
          </button>
          <button
            onClick={submit}
            disabled={saving}
            data-testid="gp-excluir-confirmar"
            className="flex items-center gap-2 rounded-md bg-red-600 px-4 py-2 text-sm font-bold text-white hover:bg-red-700 disabled:opacity-50"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
            Excluir permanentemente
          </button>
        </div>
      </div>
    </Modal>
  );
}

function MesclarProdutoModal({ origem, todosProdutos, onClose, onSaved }) {
  const [destinoId, setDestinoId] = useState("");
  const [saving, setSaving] = useState(false);
  const candidatos = useMemo(
    () => todosProdutos.filter((p) => p.tipo === origem.tipo && p.id !== origem.id),
    [todosProdutos, origem]
  );
  const destino = candidatos.find((p) => p.id === destinoId) || null;
  const saldoOrigem = Number(origem.saldo_kg || 0);
  const saldoDest = destino ? Number(destino.saldo_kg || 0) : 0;
  const saldoFinal = destino ? saldoOrigem + saldoDest : null;

  const submit = async () => {
    if (!destinoId) { toast.error("Escolha o produto de destino"); return; }
    setSaving(true);
    try {
      await api.post("/gola-punho/mesclar", { origem_id: origem.id, destino_id: destinoId });
      toast.success(`Saldo migrado (${saldoOrigem.toFixed(2).replace(".", ",")} kg) — origem removida`);
      onSaved();
    } catch (e) {
      const _m = apiError(e); if (_m) toast.error(_m);
    } finally {
      setSaving(false);
    }
  };

  const inputCls =
    "h-11 w-full rounded-md border-2 border-slate-300 bg-white px-3 text-sm font-medium outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200";

  return (
    <Modal onClose={onClose} testid="gp-mesclar-modal" title="Mesclar em outro produto">
      <div className="space-y-4">
        <div className="rounded-md bg-slate-50 p-3">
          <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Origem (será removida)</div>
          <div className="mt-0.5 font-bold text-slate-900" data-testid="gp-mesclar-origem-nome">
            {origem.tipo} — {origem.cor}
          </div>
          <div className="mt-1 font-mono text-lg font-bold text-slate-700" data-testid="gp-mesclar-origem-saldo">
            {saldoOrigem.toFixed(2).replace(".", ",")} kg
          </div>
        </div>

        <label className="block">
          <span className="mb-1 block text-sm font-semibold text-slate-700">Destino (mesmo tipo)</span>
          <select
            value={destinoId}
            onChange={(e) => setDestinoId(e.target.value)}
            className={inputCls}
            data-testid="gp-mesclar-destino"
          >
            <option value="">Selecione…</option>
            {candidatos.map((p) => (
              <option key={p.id} value={p.id}>
                {p.cor} ({Number(p.saldo_kg).toFixed(2).replace(".", ",")} kg)
              </option>
            ))}
          </select>
          {candidatos.length === 0 && (
            <div className="mt-2 rounded-md border-2 border-amber-200 bg-amber-50 p-2 text-xs font-semibold text-amber-800">
              Não há outros produtos deste tipo pra mesclar.
            </div>
          )}
        </label>

        {destino && (
          <div className="rounded-md border-2 border-emerald-200 bg-emerald-50 p-3" data-testid="gp-mesclar-preview">
            <div className="text-xs font-bold uppercase tracking-wider text-emerald-700">Resultado após mesclar</div>
            <div className="mt-0.5 font-bold text-slate-900">{destino.tipo} — {destino.cor}</div>
            <div className="mt-1 text-xs text-slate-600">
              {saldoDest.toFixed(2).replace(".", ",")} kg + {saldoOrigem.toFixed(2).replace(".", ",")} kg =
            </div>
            <div className="font-mono text-2xl font-bold text-emerald-700" data-testid="gp-mesclar-preview-saldo">
              {saldoFinal.toFixed(2).replace(".", ",")} kg
            </div>
          </div>
        )}

        <p className="text-xs text-slate-500">
          A mesclagem fica registrada no Jornal com saldos antes/depois, seu nome e a data.
        </p>

        <div className="flex justify-end gap-2 pt-2">
          <button
            onClick={onClose}
            className="rounded-md border-2 border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100"
            data-testid="gp-mesclar-cancelar"
            disabled={saving}
          >
            Cancelar
          </button>
          <button
            onClick={submit}
            disabled={saving || !destino}
            data-testid="gp-mesclar-confirmar"
            className="flex items-center gap-2 rounded-md bg-slate-900 px-4 py-2 text-sm font-bold text-white hover:bg-slate-800 disabled:opacity-50"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <GitMerge size={14} />}
            Mesclar
          </button>
        </div>
      </div>
    </Modal>
  );
}


function Modal({ onClose, title, children, testid }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-slate-900/60 p-4 sm:items-center"
      data-testid={testid}
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-md border-2 border-slate-200 bg-white p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-bold text-slate-900">{title}</h2>
          <button onClick={onClose} className="rounded-md p-1 text-slate-500 hover:bg-slate-100" data-testid={`${testid}-close`}>
            <X size={18} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
